'use client';

import React, { memo } from 'react';
import Image from 'next/image';

const capabilities = [
  {
    img: '/images/cap-flexographic.jpg',
    alt: 'Inline UV flexographic printing press with colorful ink rollers running vibrant labels',
    title: 'Multi Color Flexographic Printing',
    body: 'High speed printing systems designed for stable color consistency and scalable production.',
  },
  {
    img: '/images/cap-lamination.jpg',
    alt: 'Precision die cutting and lamination process on premium labels',
    title: 'Lamination & Finishing',
    body: 'Gloss, matte, cold foil, embossing, spot UV and protective finishing options for improved shelf appearance.',
  },
  {
    img: '/images/cap-foiling-v3.jpg',
    alt: 'Close-up of Royal Prime Labels gold hot foil and holographic cold foil finish on a premium label',
    title: 'Die Cutting & Shape Customization',
    body: 'Application specific label sizes and formats for different packaging requirements.',
  },
  {
    img: '/images/cap-inspection.jpg',
    alt: 'Quality control technician inspecting printed labels under magnifying lamp in lab',
    title: 'Inspection & Quality Checks',
    body: 'Multiple inspection stages help maintain accurate registration, adhesion performance and print consistency.',
  },
];

function AboutCapabilities() {
  return (
    <section
      aria-labelledby="capabilities-heading"
      className="w-full bg-white py-14 md:py-16 lg:py-20"
    >
      <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 md:px-8">
        {/* Heading + intro */}
        <header className="text-center max-w-[920px] mx-auto">
          <h2
            id="capabilities-heading"
            className="text-slate-900 font-extrabold tracking-[-0.01em]
                       text-[clamp(26px,4.2vw,36px)] leading-[1.2]"
          >
            Our Advanced Label Printing Capabilities
          </h2>
          <p
            className="mt-3 md:mt-4 text-slate-700
                       text-[clamp(16px,2.2vw,18px)]
                       leading-[clamp(24px,3.2vw,28px)]"
          >
            At Royal Prime, it's not just what we print, it's how we deliver it that makes the
            difference. Our operations are housed in a state-of-the-art production facility designed
            to meet the highest standards of the labeling industry.
          </p>
        </header>

        {/* Divider */}
        <div className="mt-6 md:mt-8 h-px w-full bg-slate-200/80"></div>

        {/* Capabilities grid */}
        <div
          className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          role="list"
          aria-label="Capabilities"
        >
          {capabilities.map(({ img, alt, title, body }) => (
            <article
              key={title}
              role="listitem"
              className="flex flex-col rounded-[14px] overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              {/* Image */}
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <Image
                  src={img}
                  alt={alt}
                  fill
                  className="object-cover hover:scale-105 transition-transform duration-500"
                  sizes="(min-width:1024px) 25vw, (min-width:640px) 50vw, 100vw"
                />
              </div>

              {/* Text */}
              <div className="flex flex-col gap-2 px-5 py-5">
                <h3 className="text-slate-900 font-semibold text-[16px] leading-[1.35]">
                  {title}
                </h3>
                <p className="text-slate-600 text-[14px] leading-[21px]">
                  {body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-6 md:mt-8 h-px w-full bg-slate-200/80"></div>
      </div>
    </section>
  );
}

export default memo(AboutCapabilities);
